import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { WeightedMatch } from '../hooks/useMatching';
import { Avatar } from './Avatar';
import { QueryError } from './QueryError';
import { SkeletonCard } from './Skeleton';
import { TherapistSpecializationPills } from './TherapistSpecializationPills';

interface RecommendedTherapistsProps {
  matches?: WeightedMatch[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  specializationFilter?: string;
}

export function RecommendedTherapists({
  matches = [],
  isLoading,
  isError,
  onRetry,
  specializationFilter,
}: RecommendedTherapistsProps) {
  const { t } = useTranslation();

  const browseUrl = specializationFilter
    ? `/therapists?specialization=${encodeURIComponent(specializationFilter)}`
    : '/therapists';

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        {[1, 2].map((i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  if (isError) {
    return <QueryError message={t('common.error')} onRetry={onRetry} />;
  }

  if (matches.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/80 p-8 text-center">
        <p className="text-sm text-gray-600">{t('common.noResults')}</p>
        <Link to={browseUrl} className="btn-primary mt-4 inline-block text-sm">
          {t('nav.therapists')}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Based on your assessment — you can always browse all therapists freely.
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        {matches.map((match) => (
          <Link
            key={match.therapist.id}
            to={`/therapists/${match.therapist.id}`}
            className="therapist-card group block h-full"
          >
            <div className="therapist-card-accent" />
            <div className="p-5">
              <div className="flex items-start gap-4">
                <Avatar name={match.therapist.displayName} size="md" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <h3 className="therapist-name group-hover:text-primary-700 text-base">
                      {match.therapist.displayName}
                    </h3>
                    <span className="text-primary-600 shrink-0 text-sm font-bold">
                      ₦{match.therapist.hourlyRateNgn.toLocaleString()}
                    </span>
                  </div>
                  <span className="badge-green mt-2 text-[10px] font-semibold">
                    {Math.round(match.score * 100)}% match
                  </span>
                  <div className="mt-3">
                    <TherapistSpecializationPills
                      specializations={match.therapist.specializations}
                      limit={3}
                      label="Specializes in"
                    />
                  </div>
                  <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-gray-600">
                    {match.therapist.bio}
                  </p>
                  <ul className="mt-3 space-y-1">
                    {match.reasons.map((reason) => (
                      <li key={reason} className="text-xs text-gray-500">
                        • {reason}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
      <div className="flex justify-end">
        <Link to={browseUrl} className="btn-ghost text-sm">
          Browse all therapists →
        </Link>
      </div>
    </div>
  );
}
